import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Flex, Image, Progress, Row, Select, Skeleton, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { useApi, type Me } from '../../app/api-context';
import { useSession } from '../../app/session-context';
import { JOB_STATE, STAGE_LABEL, batchProgress, won, type BatchDetail, type GenAsset, type GenBatch, type GenConfig, type GenJob, type RunNextResult } from '../../lib/generation-types';
import { vocabLabel, type VocabItem } from '../../lib/vocab-types';

const MAX_CONSECUTIVE_ERRORS = 3;

/** 대기 작업을 하나씩 처리한다. 화면을 떠나면 멈춘다(대기 작업은 남아 다음에 이어서 처리). */
function useRunner(onTick: () => Promise<unknown>, wait = (ms: number) => new Promise((r) => setTimeout(r, ms))) {
  const api = useApi();
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const stop = useRef(false);
  useEffect(() => () => {
    stop.current = true;
  }, []);

  const start = async () => {
    stop.current = false;
    setRunning(true);
    setMessage(null);
    let errors = 0;
    while (!stop.current) {
      try {
        const r = await api.post<RunNextResult>('/generation/run-next');
        errors = 0;
        await onTick();
        if (!r.processed) {
          setMessage({ type: 'success', text: '대기 중인 작업을 모두 처리했습니다.' });
          break;
        }
      } catch (e) {
        // 응답이 끊겨도(예: 프록시 시간 초과) 서버는 작업을 끝까지 처리하므로 상태를 다시 읽고 이어간다
        errors++;
        await onTick();
        if (errors >= MAX_CONSECUTIVE_ERRORS) {
          setMessage({ type: 'error', text: `연속 ${errors}번 실패해 멈췄습니다: ${(e as Error).message}` });
          break;
        }
        setMessage({ type: 'warning', text: `응답 오류(${(e as Error).message}). 상태를 확인하고 이어서 처리합니다.` });
        await wait(3000);
      }
    }
    setRunning(false);
  };
  return { running, message, start, stop: () => { stop.current = true; } };
}

function AssetCard({ a, jobs, vocabName }: { a: GenAsset; jobs: GenJob[]; vocabName: (id: string) => string }) {
  const stage = STAGE_LABEL[a.stage] ?? { label: a.stage, color: 'default' };
  const job = jobs.find((j) => j.jobId === a.jobId);
  const src = a.finalUrl ?? a.originalUrl;
  return (
    <Card size="small" styles={{ body: { padding: 8 } }} cover={src ? <Image src={src} height={160} style={{ objectFit: 'contain', background: 'repeating-conic-gradient(#f3f3f3 0% 25%, #fff 0% 50%) 50% / 16px 16px' }} alt={vocabName(a.vocabIds[0])} /> : undefined}>
      <Flex justify="space-between" align="center" gap={4}>
        <Typography.Text strong ellipsis>{vocabName(a.vocabIds[0])}</Typography.Text>
        <Tag color={stage.color} style={{ marginInlineEnd: 0 }}>{stage.label}</Tag>
      </Flex>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
        {a.origin === 'manual' ? '수동 제작 · ' : job?.kind === 'transparent-fallback' ? '투명 재생성 · ' : ''}{a.generation?.modelId.replace('openai/', '')} · {a.generation?.costKrw != null ? won(a.generation.costKrw) : '비용 미상'}
      </Typography.Text>
      {a.postprocess?.state === 'failed' && (
        <Tooltip title={a.postprocess.reasons.join(' / ')}>
          <Typography.Text type="warning" style={{ fontSize: 12 }} ellipsis>{a.postprocess.reasons[0] ?? '후처리 실패'}</Typography.Text>
        </Tooltip>
      )}
      <Flex gap={8} style={{ marginTop: 4, fontSize: 12 }}>
        {a.originalUrl && <a href={a.originalUrl} target="_blank" rel="noreferrer">원본</a>}
        {a.finalUrl && <a href={a.finalUrl} target="_blank" rel="noreferrer">최종 512</a>}
      </Flex>
    </Card>
  );
}

export function ImagesPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const me = useQuery({ queryKey: ['me', user?.email ?? null], queryFn: () => api.get<Me>('/me'), staleTime: 5 * 60_000, retry: false });
  const config = useQuery({ queryKey: ['generation', 'config'], queryFn: () => api.get<GenConfig>('/generation/config'), retry: false });
  const batches = useQuery({ queryKey: ['generation', 'batches'], queryFn: () => api.get<{ items: GenBatch[] }>('/generation/batches'), retry: false });
  const vocab = useQuery({ queryKey: ['vocab', 'list'], queryFn: () => api.get<{ items: VocabItem[] }>('/vocab'), retry: false });
  const batchId = params.get('batch') ?? batches.data?.items[0]?.batchId ?? null;
  const detail = useQuery({
    queryKey: ['generation', 'batch', batchId],
    queryFn: () => api.get<BatchDetail>(`/generation/batch?${new URLSearchParams({ id: batchId! })}`),
    enabled: !!batchId,
    retry: false,
  });
  const runner = useRunner(() => qc.invalidateQueries({ queryKey: ['generation'] }));
  const canWrite = me.data?.role === 'editor';
  const names = new Map((vocab.data?.items ?? []).map((v) => [v.vocabId, vocabLabel(v)]));
  const vocabName = (id: string) => names.get(id) ?? id;
  const b = config.data?.budget;

  const prog = detail.data ? batchProgress(detail.data.jobs) : null;

  return (
    <>
      <Typography.Title level={3}>이미지 제작</Typography.Title>
      <Typography.Paragraph type="secondary">
        어휘 관리에서 어휘를 골라 생성을 요청하면 여기서 처리합니다. 생성 → 원본 보관 → 후처리(배경 제거·512 WebP) → 검수 대기 순서로 진행됩니다.
      </Typography.Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title={`이번 달 사용(${b?.monthKey ?? '-'})`} value={b ? won(b.spentKrw) : '-'} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="처리 중 예약" value={b ? won(b.reservedKrw) : '-'} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="남은 한도" value={b ? won(b.limitKrw - b.spentKrw - b.reservedKrw) : '-'} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="월 한도" value={b ? won(b.limitKrw) : '-'} /></Card></Col>
      </Row>
      {config.data && !config.data.configured && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="이미지 생성 자격증명이 설정되지 않아 조회만 할 수 있습니다." />}
      {config.isError && <Alert type="error" showIcon style={{ marginBottom: 16 }} message="생성 설정을 불러오지 못했습니다" description={(config.error as Error).message} />}

      <Card
        size="small"
        title="생성 요청"
        extra={
          batches.data?.items.length ? (
            <Select
              value={batchId}
              onChange={(v) => setParams({ batch: v })}
              style={{ minWidth: 280 }}
              options={batches.data.items.map((x) => ({ value: x.batchId, label: `${new Date(x.createdAt).toLocaleString('ko-KR')} · ${x.kind === 'manual' ? '수동 · ' : ''}어휘 ${x.vocabIds.length}개 × ${x.candidates}장` }))}
            />
          ) : null
        }
      >
        {batches.isPending && <Skeleton active />}
        {batches.data && !batches.data.items.length && (
          <Empty description={<>아직 생성 요청이 없습니다. <Link to="/vocab">어휘 관리</Link>에서 어휘를 골라 요청하세요.</>} />
        )}
        {detail.isError && <Alert type="error" showIcon message="요청 내용을 불러오지 못했습니다" description={(detail.error as Error).message} />}
        {detail.data && prog && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Flex wrap gap={16} align="center">
              <Progress type="circle" size={64} percent={prog.total ? Math.round((prog.done / prog.total) * 100) : 0} />
              <div>
                <div>작업 {prog.total}건 · 완료 {prog.succeeded} · 실패 {prog.failed} · 대기 {prog.queued} · 처리 중 {prog.running}</div>
                <Typography.Text type="secondary">실제 비용 {won(prog.cost)} (예상 최대 {won(detail.data.batch.estimateKrw)}{detail.data.batch.maxEstimateKrw > detail.data.batch.estimateKrw ? `, 재생성 포함 ${won(detail.data.batch.maxEstimateKrw)}` : ''})</Typography.Text>
              </div>
              {canWrite && config.data?.configured && (
                <Space style={{ marginInlineStart: 'auto' }}>
                  {runner.running ? (
                    <Button onClick={runner.stop}>멈춤(진행 중인 1건은 마저 처리)</Button>
                  ) : (
                    <Button type="primary" disabled={prog.queued === 0} onClick={() => void runner.start()}>
                      처리 시작{prog.queued ? ` (대기 ${prog.queued}건)` : ''}
                    </Button>
                  )}
                </Space>
              )}
            </Flex>
            {runner.running && <Alert type="info" showIcon message="처리 중입니다. 이 화면을 열어 두세요. 1장에 보통 수십 초 걸립니다." />}
            {runner.message && <Alert type={runner.message.type} showIcon message={runner.message.text} />}

            {detail.data.assets.length > 0 && (
              <Row gutter={[12, 12]}>
                {[...detail.data.assets].sort((x, y) => x.assetId.localeCompare(y.assetId)).map((a) => (
                  <Col key={a.assetId} xs={12} sm={8} md={6} lg={4}><AssetCard a={a} jobs={detail.data.jobs} vocabName={vocabName} /></Col>
                ))}
              </Row>
            )}

            <Table<GenJob>
              size="small"
              rowKey="jobId"
              pagination={false}
              dataSource={detail.data.jobs}
              columns={[
                { title: '어휘', dataIndex: 'vocabId', render: (id: string) => vocabName(id) },
                { title: '모델', dataIndex: 'modelId', render: (m: string, j) => <>{m.replace('openai/', '')}{j.kind === 'transparent-fallback' && <Tag style={{ marginInlineStart: 4 }}>투명 재생성</Tag>}</> },
                { title: '상태', dataIndex: 'state', render: (s: GenJob['state']) => <Tag color={JOB_STATE[s].color}>{JOB_STATE[s].label}</Tag> },
                { title: '결과', render: (_, j) => (j.outcome === 'succeeded' ? '검수 대기' : j.outcome === 'rework' ? <Tooltip title={j.reasons.join(' / ')}>재작업</Tooltip> : j.error ? <Typography.Text type="danger" ellipsis={{ tooltip: j.error }} style={{ maxWidth: 260 }}>{j.error}</Typography.Text> : '-') },
                { title: '비용', dataIndex: 'costKrw', align: 'right', render: (c: number | null) => (c != null ? won(c) : '-') },
              ]}
            />
          </Space>
        )}
      </Card>
    </>
  );
}
